import { useQuery } from "@tanstack/react-query";
import { FC, ReactNode } from "react";
import { DateTime } from "luxon";
import styled from "styled-components";

import { api } from "@/utils/api";
import { Table, TableContent, Td, Th } from "@/components/Table";
import Text from "@/components/Text";
import {
  ParticipantHolder,
  StyledScrollableTable,
  TableHolder,
} from "./ScoreByWins";
import { ReactComponent as GoldMedal } from "@/assets/icons/gold-medal.svg";
import { ReactComponent as SilverMedal } from "@/assets/icons/silver-medal.svg";
import { ReactComponent as BronzeMedal } from "@/assets/icons/bronze-medal.svg";

const StyledTable = styled(Table)`
  border-collapse: separate;
  th,
  td {
    font-weight: 700;
    white-space: nowrap;

    &:first-child {
      min-width: 200px;
      padding-left: 24px;
      white-space: normal;
      min-width: 200px;
      padding-left: 24px;
      width: 200px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      position: sticky;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 1;
      padding-right: 16px;
      padding-left: 16px;
      border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
  }
  td {
    padding: 16px;
  }
  td[data-wins="false"] {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Wrap = styled.div`
  margin-bottom: 56px;
`;

const TableHeader = styled.div`
  padding: 24px;
`;

const Heading = styled(Text)`
  span {
    font-weight: 400;
  }
`;

interface Data {
  maxByDays: {
    [date: string]: number;
  };
  users: {
    name: string;
    user_id: number;
    topCtr: number;
    avgCtr: number;
    maxCtrs: {
      [date: string]: number;
    };
  }[];
  winners: number[];
}

interface Props {
  activePeriod: string;
  startDt: DateTime;
  endDt: DateTime;
  renderThs: () => ReactNode[];
  sequenceValue: {
    value: number | "All";
    type: string;
  };
}

const ScoreByCtr: FC<Props> = ({
  activePeriod,
  startDt,
  endDt,
  renderThs,
  sequenceValue,
}) => {
  const { data, isLoading, isError } = useQuery(
    [
      "leaderboard_scores",
      { type: "ctr", period: activePeriod, sequenceValue },
    ],
    () => {
      return api.get<never, Data>("/v1/gamification/thumbnails/score-by-ctr", {
        params: {
          date_from: activePeriod,
          date_to: DateTime.fromFormat(activePeriod, "yyyy-LL-dd")
            .endOf("month")
            .toFormat("yyyy-LL-dd"),
          ...(sequenceValue?.value !== "All" && {
            ...(sequenceValue?.type === "C"
              ? { c_batch: sequenceValue.value }
              : { batch: sequenceValue.value }),
          }),
        },
      });
    }
  );

  const renderTds = (ctrs: { [date: string]: number }, user_id: number) => {
    const components: ReactNode[] = [];

    let days = 0;

    while (startDt.plus({ days }).toFormat("dd") <= endDt.toFormat("dd")) {
      const dailyWinner =
        data?.maxByDays[startDt.plus({ days }).toFormat("yyyy-LL-dd")] ===
        user_id;

      components.push(
        <Td key={days} data-wins={dailyWinner}>
          {ctrs[startDt.plus({ days }).toFormat("yyyy-LL-dd")]
            ? `${ctrs[startDt.plus({ days }).toFormat("yyyy-LL-dd")]} %`
            : "-"}
        </Td>
      );

      days++;

      if (days === parseInt(endDt.toFormat("dd"))) {
        break;
      }
    }

    return components;
  };

  const renderMedal = (userId: number) => {
    if (data?.winners.indexOf(userId) === 0) {
      return <GoldMedal />;
    }
    if (data?.winners.indexOf(userId) === 1) {
      return <SilverMedal />;
    }
    if (data?.winners.indexOf(userId) === 2) {
      return <BronzeMedal />;
    }
  };

  return (
    <Wrap>
      <TableHolder>
        <TableHeader>
          <Heading $type="smallBold">
            Participants Score by Top CTR (%){" "}
            <span>
              · {sequenceValue.type}
              {sequenceValue.value} ·{" "}
              {DateTime.fromISO(activePeriod).toFormat("yyyy LLLL")}
            </span>
          </Heading>
        </TableHeader>
        <StyledScrollableTable>
          <StyledTable>
            <thead>
              <tr>
                <Th>Participant</Th>
                {renderThs()}
                <Th>Top CTR</Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!data?.users?.length}
              >
                {data?.users.map((user, index) => (
                  <tr key={index}>
                    <Td>
                      <ParticipantHolder>
                        {user.name} {renderMedal(user.user_id)}
                      </ParticipantHolder>
                    </Td>
                    {renderTds(user.maxCtrs, user.user_id)}
                    <Td>{user.topCtr} %</Td>
                  </tr>
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </StyledScrollableTable>
      </TableHolder>
    </Wrap>
  );
};

export default ScoreByCtr;
